import React, { useState, useEffect, useContext, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useMutation, useApolloClient } from "react-apollo";
import { Tag, Title, Tab, Field, Control, Button, Icon } from "rbx";
import {
  Link,
  Route,
  useParams,
  useHistory,
  useLocation,
  Switch
} from "react-router-dom";
import Barcode from "react-barcode";
import {
  formatCurrency,
  unmaskCurrency,
  ourToast as toast
} from "../../../utils";
import { ModalContext } from "../../../components/ModalProvider";
import { AddItemVendorForm } from "../../../components/Vendors";
import Loading from "../../../components/Loading";

import {
  SINGLE_ITEM_QUERY,
  UPDATE_ITEM_MUTATION
} from "../../../graphql/Items/Items.graphql";
import { ALL_INVENTORY_ON_HAND_QUERY } from "../../../graphql/Items/InventoryOnHand.graphql";

import Details from "./routes/Details";

import ConfirmItemDelete from "./ConfirmItemDelete";

import { useSystemCodes } from "../../../utils/hooks";

import PrintBarcodeModal from "../../../components/PrintBarcodeModal";

import "./ItemPage.scss";

const INITIAL_STATE = {
  ItemNo: "",
  Description: "",
  StdCost: "",
  AvgCost: "",
  ListPrice: "",
  MinQty: "",
  MaxQty: "",
  AllowDiscounts: "",
  ItemCatgRecIdRef: "",
  Active: "",
  InvGLRecIdRef: "",
  CogsGLRecIdRef: ""
};

const ItemPage = () => {
  const { data: systemCodes } = useSystemCodes({
    Category: "General Ledger",
    CodeName: "AcctType"
  });

  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { setModalOpen } = useContext(ModalContext);

  const client = useApolloClient();
  const { data } = useQuery(SINGLE_ITEM_QUERY, {
    variables: { where: { RecId: parseInt(id) } }
  });
  const [updateItem, { loading }] = useMutation(UPDATE_ITEM_MUTATION, {
    refetchQueries: ["ALL_ITEMS_QUERY", "SINGLE_ITEM_QUERY"]
  });

  const [editing, setEditing] = useState(true);
  const [inputs, setInputs] = useState({ ...INITIAL_STATE });
  const [codes, setCodes] = useState(null);

  const getItemInformation = useCallback(
    async Item => {
      if (Item) {
        const OHData = await client.query({
          query: ALL_INVENTORY_ON_HAND_QUERY,
          variables: {
            where: { ItemRecIdRef: parseInt(Item.RecId) }
          },
          fetchPolicy: "network-only"
        });
        setInputs({
          ...Item,
          LastPurchaseCost: Item.LastPurchaseCost
            ? formatCurrency(Item.LastPurchaseCost)
            : null,
          StdCost: Item.StdCost ? formatCurrency(Item.StdCost) : null,
          AvgCost: Item.AvgCost ? formatCurrency(Item.AvgCost) : null,
          ListPrice: Item.ListPrice ? formatCurrency(Item.ListPrice) : null,
          OH:
            (OHData &&
              OHData.data &&
              OHData.data.InventoryOnHands &&
              Array.isArray(OHData.data.InventoryOnHands) &&
              OHData.data.InventoryOnHands.length > 0 &&
              OHData.data.InventoryOnHands.reduce(
                (a, b) => a + (b.QtyOnHand || 0),
                0
              )) ||
            "0"
        });
      }
    },
    [client]
  );

  useEffect(() => {
    if (data?.Item) {
      getItemInformation(data?.Item);
    }
  }, [data, getItemInformation]);

  useEffect(() => {
    if (!codes && Array.isArray(systemCodes)) {
      setCodes(systemCodes);
    }
  }, [codes, systemCodes]);

  const toggleEditing = () => setEditing(!editing);
  const handleChange = e => {
    let value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    if (
      e.target.name === "StdCost" ||
      e.target.name === "AvgCost" ||
      e.target.name === "ListPrice" ||
      e.target.name === "LastPurchaseCost"
    ) {
      const unmaskedValue = unmaskCurrency(e.target.value);
      value = formatCurrency(unmaskedValue);
    }
    setInputs({ ...inputs, [e.target.name]: value });
  };

  const handleSave = async e => {
    e.preventDefault();
    try {
      const obj = Object.keys(INITIAL_STATE).reduce(
        (acc, curr) =>
          inputs[curr] !== null ? { ...acc, [curr]: inputs[curr] } : acc,
        {}
      );
      await updateItem({
        variables: {
          data: {
            ...obj,
            StdCost: obj.StdCost
              ? parseFloat(unmaskCurrency(obj.StdCost))
              : 0.0,
            AvgCost: obj.AvgCost
              ? parseFloat(unmaskCurrency(obj.AvgCost))
              : 0.0,
            ListPrice: obj.ListPrice
              ? parseFloat(unmaskCurrency(obj.ListPrice))
              : null,
            MinQty: obj.MinQty ? parseFloat(obj.MinQty) : null,
            MaxQty: obj.MaxQty ? parseFloat(obj.MaxQty) : null,
            ItemCatgRecIdRef: parseInt(obj.ItemCatgRecIdRef),
            InvGLRecIdRef: obj.InvGLRecIdRef
              ? parseInt(obj.InvGLRecIdRef)
              : null,
            CogsGLRecIdRef: obj.CogsGLRecIdRef
              ? parseInt(obj.CogsGLRecIdRef)
              : null
          },
          where: { RecId: parseInt(id) }
        }
      });
      setEditing(false);
      toast.success("Item updated successfully.");
    } catch (err) {
      toast.error("Error updating Item.");
    }
  };

  const handleClickDelete = () => {
    setModalOpen(true, <ConfirmItemDelete history={history} id={id} />);
  };

  const addItemVendor = () => {
    setModalOpen(
      true,
      <AddItemVendorForm
        ItemRecIdRef={parseInt(id)}
        onComplete={() => setModalOpen(false, "")}
      />
    );
  };

  const handlePrintBarcode = () => {
    setModalOpen(
      true,
      <PrintBarcodeModal
        ItemNo={inputs.ItemNo}
        onComplete={() => setModalOpen(false, "")}
      />
    );
  };

  if (loading || !data?.Item || !codes) return <Loading />;
  const getActionButtons = () => {
    if (location.pathname.includes("details")) {
      return (
        <Field kind="group">
          <Control>
            <Button color="info" size="small" onClick={handlePrintBarcode}>
              <Icon>
                <FontAwesomeIcon icon="barcode" />
              </Icon>
              <span>Print Barcode</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="primary"
              disabled={editing}
              size="small"
              onClick={toggleEditing}
            >
              <Icon>
                <FontAwesomeIcon icon="edit" />
              </Icon>
              <span>Edit</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="danger"
              size="small"
              state={loading ? "loading" : ""}
              onClick={handleClickDelete}
            >
              <Icon>
                <FontAwesomeIcon icon="trash" />
              </Icon>
              <span>Delete</span>
            </Button>
          </Control>
          <Control>
            <Button
              color="success"
              disabled={!editing}
              size="small"
              onClick={handleSave}
            >
              <Icon>
                <FontAwesomeIcon icon="save" />
              </Icon>
              <span>Save</span>
            </Button>
          </Control>
        </Field>
      );
    }
  };
  return (
    <React.Fragment>
      <header className="page-head">
        <div className="page-head-start">
          <Title color="primary" size={3}>
            {inputs.ItemNo && inputs.Description
              ? `${inputs.ItemNo} ${inputs.Description}`
              : ""}
          </Title>
          <Tag color={inputs?.Active ? "success" : "danger"}>
            {inputs?.Active ? "ACTIVE" : "INACTIVE"}
          </Tag>
          <div className="page-head-start--subheader">
            <Barcode
              fontSize={12}
              height={25}
              value={inputs.ItemNo}
              width={1}
            />
          </div>
        </div>
        <div className="page-head-end">{getActionButtons(location)}</div>
      </header>
      <Tab.Group kind="boxed">
        <Tab
          active={location.pathname.includes("details")}
          as={Link}
          tabIndex="-1"
          to="details"
        >
          <span>Details</span>
        </Tab>
      </Tab.Group>
      <Switch>
        <Route path="/inventory/items/:id/details">
          <Details
            addItemVendor={addItemVendor}
            codes={codes}
            editing={editing}
            item={inputs}
            setModalOpen={setModalOpen}
            onChange={handleChange}
          />
        </Route>
      </Switch>
    </React.Fragment>
  );
};

ItemPage.propTypes = {};

export default ItemPage;
